import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";

// ─── Constantes ───────────────────────────────────────────────────────────────
const DAYS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const DAYS_FULL = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const SERVICE_LABELS = {
  "jardinero": "Jardinería", "plomeros": "Plomería", "electricista": "Electricista",
  "gasista": "Gasista", "albanileria": "Albañilería", "pintura": "Pintores",
  "cerrajeria": "Cerrajería", "aire-acondicionado": "Aire Acond.", "tecnologia": "Tecnología",
  "mudanzas": "Mudanzas", "limpieza": "Limpieza", "carpinteria": "Carpintería",
};

const STATUS_CONFIG = {
  scheduled:   { label: "Agendado",   color: "#163244", bg: "#e7f0f5", dot: "#163244" },
  in_progress: { label: "En curso",   color: "#b06a00", bg: "#fff3e0", dot: "#E7A432" },
  completed:   { label: "Completado", color: "#1b7f4e", bg: "#e8f7ef", dot: "#1b7f4e" },
  cancelled:   { label: "Cancelado",  color: "#c0392b", bg: "#fdecea", dot: "#c0392b" },
};

const BLOCK_REASONS = ["Personal", "Vacaciones", "Feriado", "Enfermedad", "Capacitación", "Otro"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getMondayOf(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function formatDate(date) {
  return new Date(date).toLocaleDateString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

function formatTime(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}

function isSameDay(d1, d2) {
  const a = new Date(d1), b = new Date(d2);
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth()    === b.getMonth()    &&
         a.getDate()     === b.getDate();
}

function isToday(date) {
  return isSameDay(date, new Date());
}

// ─── Job Card ─────────────────────────────────────────────────────────────────
function JobCard({ job, onClick }) {
  const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.scheduled;
  const serviceLabel = SERVICE_LABELS[job.service_key] || job.service_key;
  const time = formatTime(job.scheduled_start);

  return (
    <div
      onClick={() => onClick(job)}
      style={{
        background: cfg.bg,
        border: `1.5px solid ${cfg.dot}33`,
        borderLeft: `3px solid ${cfg.dot}`,
        borderRadius: 6, padding: "6px 8px",
        marginBottom: 4, cursor: "pointer",
        transition: "transform 0.1s, box-shadow 0.1s",
        fontSize: 11,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 3px 10px rgba(22,50,68,0.12)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
        <div style={{
          width: 6, height: 6, borderRadius: "50%",
          background: cfg.dot, flexShrink: 0,
        }} />
        <span style={{ fontWeight: 700, color: cfg.color, fontSize: 10, letterSpacing: "0.04em" }}>
          {cfg.label.toUpperCase()}
        </span>
      </div>
      <div style={{ fontWeight: 700, color: "#163244", fontSize: 12, marginBottom: 1 }}>
        {serviceLabel}
      </div>
      <div style={{ color: "#5c7a8a", fontSize: 11 }}>
        👤 {job.client_name}
      </div>
      {time && !job.all_day && (
        <div style={{ color: "#8fa3b1", fontSize: 10, marginTop: 2 }}>
          🕐 {time}hs
        </div>
      )}
      {job.all_day && (
        <div style={{ color: "#8fa3b1", fontSize: 10, marginTop: 2 }}>
          Todo el día
        </div>
      )}
    </div>
  );
}

// ─── Block Chip ───────────────────────────────────────────────────────────────
function BlockChip({ block, onClick }) {
  return (
    <div
      onClick={() => onClick(block)}
      style={{
        background: "#fdecea", border: "1.5px solid #e0a0a0",
        borderLeft: "3px solid #c0392b",
        borderRadius: 6, padding: "6px 8px",
        marginBottom: 4, cursor: "pointer",
        fontSize: 11,
      }}
    >
      <div style={{ fontWeight: 700, color: "#c0392b", fontSize: 10, marginBottom: 2 }}>
        🚫 BLOQUEADO
      </div>
      <div style={{ color: "#c0392b", fontWeight: 600 }}>{block.reason}</div>
    </div>
  );
}

// ─── Job Edit Modal ───────────────────────────────────────────────────────────
function JobModal({ job, onClose, onSave, onStatusChange }) {
  const [form, setForm] = useState({
    scheduled_start: job.scheduled_start
      ? new Date(job.scheduled_start).toISOString().slice(0, 16)
      : "",
    scheduled_end: job.scheduled_end
      ? new Date(job.scheduled_end).toISOString().slice(0, 16)
      : "",
    all_day: job.all_day ?? true,
    notes: job.notes || "",
    estimated_duration_hs: job.estimated_duration_hs || "",
  });
  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState("");
  const [tab,     setTab]       = useState("info"); // info | edit

  const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.scheduled;
  const serviceLabel = SERVICE_LABELS[job.service_key] || job.service_key;

  const NEXT_STATUSES = {
    scheduled:   [{ value: "in_progress", label: "▶ Iniciar trabajo" }],
    in_progress: [{ value: "completed",   label: "✓ Marcar completado" }],
  };
  const nextActions = NEXT_STATUSES[job.status] || [];

  const handleSave = async () => {
    setError("");
    setLoading(true);
    try {
      const body = {
        all_day: form.all_day,
        notes:   form.notes || null,
        estimated_duration_hs: form.estimated_duration_hs || null,
      };
      if (!form.all_day && form.scheduled_start) {
        body.scheduled_start = new Date(form.scheduled_start).toISOString();
      }
      if (!form.all_day && form.scheduled_end) {
        body.scheduled_end = new Date(form.scheduled_end).toISOString();
      }
      if (form.all_day && form.scheduled_start) {
        // Para all_day, guardar solo la fecha sin hora
        const d = new Date(form.scheduled_start);
        d.setHours(9, 0, 0, 0);
        body.scheduled_start = d.toISOString();
      }
      await api.put(`/providers/calendar/jobs/${job.id}`, body);
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (newStatus) => {
    setLoading(true);
    try {
      await api.patch(`/providers/calendar/jobs/${job.id}/status`, { status: newStatus });
      onStatusChange();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Error al cambiar estado");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    padding: "9px 12px", borderRadius: 7, border: "1.5px solid #cddae4",
    fontSize: 13, fontFamily: "Montserrat, sans-serif", color: "#163244",
    background: "#fff", width: "100%", boxSizing: "border-box", outline: "none",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      background: "rgba(22,50,68,0.6)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "min(500px, 94vw)",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 12px 48px rgba(22,50,68,0.25)",
      }}>
        {/* Header */}
        <div style={{
          background: "#163244", padding: "20px 24px",
          borderRadius: "16px 16px 0 0",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <div>
            <div style={{ color: "#E7A432", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 4 }}>
              {serviceLabel.toUpperCase()}
            </div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 17 }}>
              {job.client_name}
            </div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              marginTop: 6, background: cfg.bg,
              borderRadius: 20, padding: "2px 10px",
            }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: cfg.color }}>{cfg.label}</span>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.1)", border: "none", color: "#fff",
            width: 30, height: 30, borderRadius: "50%", cursor: "pointer",
            fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
          }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #e0e9ef" }}>
          {["info", "edit"].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: 1, padding: "12px", border: "none",
              background: tab === t ? "#fff" : "#f7fafc",
              borderBottom: tab === t ? "2px solid #163244" : "2px solid transparent",
              color: tab === t ? "#163244" : "#8fa3b1",
              fontFamily: "Montserrat, sans-serif", fontWeight: 700,
              fontSize: 12, cursor: "pointer", letterSpacing: "0.04em",
            }}>
              {t === "info" ? "INFORMACIÓN" : "EDITAR"}
            </button>
          ))}
        </div>

        <div style={{ padding: "20px 24px" }}>
          {tab === "info" && (
            <div>
              {/* Contacto */}
              <div style={{
                background: "#f0f5f8", borderRadius: 10, padding: "14px 16px", marginBottom: 16,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8fa3b1", letterSpacing: "0.06em", marginBottom: 10 }}>
                  CONTACTO DEL CLIENTE
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <span>👤</span>
                    <span style={{ fontWeight: 700, color: "#163244", fontSize: 14 }}>{job.client_name}</span>
                  </div>
                  {job.client_phone && (
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span>📱</span>
                      <a href={`tel:${job.client_phone}`} style={{ color: "#163244", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>
                        {job.client_phone}
                      </a>
                    </div>
                  )}
                  {job.client_email && (
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span>✉️</span>
                      <a href={`mailto:${job.client_email}`} style={{ color: "#163244", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>
                        {job.client_email}
                      </a>
                    </div>
                  )}
                  {job.address && (
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span>📍</span>
                      <span style={{ color: "#5c7a8a", fontSize: 13 }}>{job.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Fecha */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8fa3b1", letterSpacing: "0.06em", marginBottom: 8 }}>
                  FECHA Y HORA
                </div>
                <div style={{ fontSize: 14, color: "#163244", fontWeight: 600 }}>
                  {job.scheduled_start
                    ? job.all_day
                      ? formatDate(job.scheduled_start)
                      : `${formatDate(job.scheduled_start)} — ${formatTime(job.scheduled_start)}hs`
                    : "Sin fecha confirmada"}
                </div>
              </div>

              {/* Notas */}
              {job.notes && (
                <div style={{
                  background: "#fffbf0", border: "1px solid #E7A432",
                  borderRadius: 8, padding: "12px 14px", marginBottom: 16,
                  fontSize: 13, color: "#163244", lineHeight: 1.6,
                }}>
                  📝 {job.notes}
                </div>
              )}

              {/* Acciones de estado */}
              {nextActions.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {nextActions.map(action => (
                    <button
                      key={action.value}
                      onClick={() => handleStatus(action.value)}
                      disabled={loading}
                      style={{
                        padding: "12px", borderRadius: 8, border: "none",
                        background: action.value === "completed" ? "#1b7f4e" : "#163244",
                        color: "#fff", fontFamily: "Montserrat, sans-serif",
                        fontWeight: 700, fontSize: 14, cursor: "pointer",
                      }}
                    >
                      {loading ? "..." : action.label}
                    </button>
                  ))}
                  {job.status !== "completed" && job.status !== "cancelled" && (
                    <button
                      onClick={() => handleStatus("cancelled")}
                      disabled={loading}
                      style={{
                        padding: "10px", borderRadius: 8,
                        border: "2px solid #e0dede", background: "transparent",
                        color: "#c0392b", fontFamily: "Montserrat, sans-serif",
                        fontWeight: 600, fontSize: 13, cursor: "pointer",
                      }}
                    >
                      Cancelar trabajo
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "edit" && (
            <div>
              {/* Todo el día toggle */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#163244", display: "block", letterSpacing: "0.06em", marginBottom: 8 }}>
                  TIPO DE AGENDA
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[{ val: true, label: "Todo el día" }, { val: false, label: "Con horario" }].map(opt => (
                    <button
                      key={String(opt.val)}
                      onClick={() => setForm(f => ({ ...f, all_day: opt.val }))}
                      style={{
                        flex: 1, padding: "9px", borderRadius: 7, cursor: "pointer",
                        border: "1.5px solid",
                        borderColor: form.all_day === opt.val ? "#163244" : "#cddae4",
                        background: form.all_day === opt.val ? "#163244" : "#fff",
                        color: form.all_day === opt.val ? "#fff" : "#5c7a8a",
                        fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13,
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fecha inicio */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#163244", display: "block", letterSpacing: "0.06em", marginBottom: 6 }}>
                  {form.all_day ? "FECHA" : "INICIO"} *
                </label>
                <input
                  type={form.all_day ? "date" : "datetime-local"}
                  style={inputStyle}
                  value={form.all_day
                    ? form.scheduled_start?.slice(0, 10) || ""
                    : form.scheduled_start || ""}
                  onChange={e => setForm(f => ({ ...f, scheduled_start: e.target.value }))}
                />
              </div>

              {/* Fecha fin (solo si tiene horario) */}
              {!form.all_day && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#163244", display: "block", letterSpacing: "0.06em", marginBottom: 6 }}>
                    FIN (opcional)
                  </label>
                  <input
                    type="datetime-local"
                    style={inputStyle}
                    value={form.scheduled_end || ""}
                    onChange={e => setForm(f => ({ ...f, scheduled_end: e.target.value }))}
                  />
                </div>
              )}

              {/* Duración estimada — solo si tiene horario */}
              {!form.all_day && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#163244", display: "block", letterSpacing: "0.06em", marginBottom: 6 }}>
                    DURACIÓN ESTIMADA (hs)
                  </label>
                  <input
                    type="number" min={0.5} max={24} step={0.5}
                    style={inputStyle}
                    placeholder="Ej: 2.5"
                    value={form.estimated_duration_hs}
                    onChange={e => setForm(f => ({ ...f, estimated_duration_hs: e.target.value }))}
                  />
                  <div style={{ fontSize: 10, color: "#8fa3b1", marginTop: 3 }}>
                    Si ingresás duración sin fecha de fin, se calcula automáticamente
                  </div>
                </div>
              )}

              {/* Notas */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#163244", display: "block", letterSpacing: "0.06em", marginBottom: 6 }}>
                  NOTAS INTERNAS
                </label>
                <textarea
                  style={{ ...inputStyle, minHeight: 80, resize: "vertical", lineHeight: 1.6 }}
                  placeholder="Recordatorios, materiales, instrucciones de acceso..."
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>

              {error && (
                <div style={{ color: "#c0392b", fontSize: 13, marginBottom: 12, fontWeight: 600 }}>
                  ⚠ {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={onClose} style={{
                  flex: 1, padding: "11px", borderRadius: 8,
                  border: "2px solid #e0e9ef", background: "transparent",
                  color: "#5c7a8a", fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600, fontSize: 14, cursor: "pointer",
                }}>
                  Cancelar
                </button>
                <button onClick={handleSave} disabled={loading} style={{
                  flex: 2, padding: "11px", borderRadius: 8, border: "none",
                  background: "#163244", color: "#fff",
                  fontFamily: "Montserrat, sans-serif", fontWeight: 700,
                  fontSize: 14, cursor: loading ? "not-allowed" : "pointer",
                }}>
                  {loading ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Block Modal ──────────────────────────────────────────────────────────────
function BlockModal({ block, onClose, onSave, onDelete }) {
  const isNew = !block;
  const [form, setForm] = useState({
    starts_at: block?.starts_at ? new Date(block.starts_at).toISOString().slice(0, 10) : "",
    ends_at:   block?.ends_at   ? new Date(block.ends_at).toISOString().slice(0, 10)   : "",
    reason:    block?.reason    || "Personal",
    all_day:   block?.all_day   ?? true,
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handleSave = async () => {
    if (!form.starts_at || !form.ends_at) {
      setError("Las fechas son requeridas");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const starts = new Date(form.starts_at);
      starts.setHours(0, 0, 0, 0);
      const ends = new Date(form.ends_at);
      ends.setHours(23, 59, 59, 999);

      await api.post("/providers/calendar/blocks", {
        starts_at: starts.toISOString(),
        ends_at:   ends.toISOString(),
        reason:    form.reason,
        all_day:   form.all_day,
      });
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Error al crear bloqueo");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/providers/calendar/blocks/${block.id}`);
      onDelete();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || "Error al eliminar bloqueo");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    padding: "9px 12px", borderRadius: 7, border: "1.5px solid #cddae4",
    fontSize: 13, fontFamily: "Montserrat, sans-serif", color: "#163244",
    background: "#fff", width: "100%", boxSizing: "border-box", outline: "none",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 300,
      background: "rgba(22,50,68,0.6)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "min(440px, 94vw)",
        boxShadow: "0 12px 48px rgba(22,50,68,0.25)",
      }}>
        <div style={{
          background: "#c0392b", padding: "20px 24px",
          borderRadius: "16px 16px 0 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>
            🚫 {isNew ? "Nuevo bloqueo" : "Editar bloqueo"}
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
            width: 28, height: 28, borderRadius: "50%", cursor: "pointer", fontSize: 16,
          }}>×</button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#163244", display: "block", letterSpacing: "0.06em", marginBottom: 6 }}>
              MOTIVO
            </label>
            <select
              style={{ ...inputStyle, cursor: "pointer" }}
              value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
            >
              {BLOCK_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#163244", display: "block", letterSpacing: "0.06em", marginBottom: 6 }}>
                DESDE *
              </label>
              <input type="date" style={inputStyle}
                value={form.starts_at}
                onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#163244", display: "block", letterSpacing: "0.06em", marginBottom: 6 }}>
                HASTA *
              </label>
              <input type="date" style={inputStyle}
                value={form.ends_at}
                onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))}
              />
            </div>
          </div>

          {error && (
            <div style={{ color: "#c0392b", fontSize: 13, marginBottom: 12, fontWeight: 600 }}>
              ⚠ {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            {!isNew && (
              <button onClick={handleDelete} disabled={loading} style={{
                padding: "11px 16px", borderRadius: 8,
                border: "2px solid #fdecea", background: "#fdecea",
                color: "#c0392b", fontFamily: "Montserrat, sans-serif",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}>
                Eliminar
              </button>
            )}
            <button onClick={onClose} style={{
              flex: 1, padding: "11px", borderRadius: 8,
              border: "2px solid #e0e9ef", background: "transparent",
              color: "#5c7a8a", fontFamily: "Montserrat, sans-serif",
              fontWeight: 600, fontSize: 14, cursor: "pointer",
            }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={loading} style={{
              flex: 2, padding: "11px", borderRadius: 8, border: "none",
              background: "#c0392b", color: "#fff",
              fontFamily: "Montserrat, sans-serif", fontWeight: 700,
              fontSize: 14, cursor: loading ? "not-allowed" : "pointer",
            }}>
              {loading ? "..." : isNew ? "Bloquear días" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProviderCalendarPage() {
  const navigate = useNavigate();

  const [weekStart,   setWeekStart]   = useState(() => getMondayOf(new Date()));
  const [calData,     setCalData]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [blockModal,  setBlockModal]  = useState(false); // false | null (new) | block obj

  const fetchWeek = useCallback(() => {
    setLoading(true);
    setError("");
    const iso = weekStart.toISOString().slice(0, 10);
    api.get(`/providers/calendar/week?week_start=${iso}`)
      .then(res => setCalData(res.data))
      .catch(() => setError("No se pudo cargar el calendario."))
      .finally(() => setLoading(false));
  }, [weekStart]);

  useEffect(() => { fetchWeek(); }, [fetchWeek]);

  const prevWeek = () => setWeekStart(d => addDays(d, -7));
  const nextWeek = () => setWeekStart(d => addDays(d,  7));
  const goToday  = () => setWeekStart(getMondayOf(new Date()));

  // Días de la semana actual
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Agrupar jobs por día
  const jobsByDay = (calData?.jobs || []).reduce((acc, job) => {
    if (!job.scheduled_start) {
      (acc["unscheduled"] = acc["unscheduled"] || []).push(job);
      return acc;
    }
    const key = new Date(job.scheduled_start).toDateString();
    (acc[key] = acc[key] || []).push(job);
    return acc;
  }, {});

  // Agrupar blocks por día (un block puede abarcar varios días)
  const blocksByDay = (calData?.blocks || []).reduce((acc, block) => {
    const start = new Date(block.starts_at);
    const end   = new Date(block.ends_at);
    days.forEach(day => {
      if (day >= start && day <= end) {
        const key = day.toDateString();
        (acc[key] = acc[key] || []).push(block);
      }
    });
    return acc;
  }, {});

  // Disponibilidad base por día de semana
  const availByDay = (calData?.availability || []).reduce((acc, a) => {
    acc[a.day_of_week] = a;
    return acc;
  }, {});

  // Stats
  const totalJobs     = (calData?.jobs || []).length;
  const pendingJobs   = (calData?.jobs || []).filter(j => j.status === "scheduled").length;
  const unscheduled   = jobsByDay["unscheduled"] || [];

  const monthLabel = (() => {
    const startMonth = MONTHS_ES[weekStart.getMonth()];
    const endDate    = addDays(weekStart, 6);
    const endMonth   = MONTHS_ES[endDate.getMonth()];
    const year       = weekStart.getFullYear();
    return startMonth === endMonth
      ? `${startMonth} ${year}`
      : `${startMonth} / ${endMonth} ${year}`;
  })();

  return (
    <>
      <div style={{
        fontFamily: "Montserrat, sans-serif",
        maxWidth: 1000, margin: "0 auto",
        padding: "24px 16px 80px",
      }}>

        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <h1 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 800, color: "#163244" }}>
              Mi agenda
            </h1>
            <p style={{ margin: 0, color: "#8fa3b1", fontSize: 14 }}>
              {totalJobs} trabajo{totalJobs !== 1 ? "s" : ""} esta semana
              {pendingJobs > 0 && ` · ${pendingJobs} pendiente${pendingJobs !== 1 ? "s" : ""}`}
            </p>
          </div>
          <button
            onClick={() => setBlockModal(null)}
            style={{
              padding: "10px 18px", borderRadius: 8,
              background: "#fdecea", border: "1.5px solid #e0a0a0",
              color: "#c0392b", fontFamily: "Montserrat, sans-serif",
              fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}
          >
            🚫 Bloquear días
          </button>
        </div>

        {/* Navegación de semana */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          marginBottom: 16, background: "#fff", borderRadius: 12,
          padding: "10px 16px", boxShadow: "0 2px 8px rgba(22,50,68,0.06)",
        }}>
          <button onClick={prevWeek} style={{
            background: "none", border: "1.5px solid #e0e9ef",
            borderRadius: 7, width: 34, height: 34, cursor: "pointer",
            color: "#163244", fontSize: 16, display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>‹</button>

          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#163244" }}>
              {monthLabel}
            </div>
            <div style={{ fontSize: 12, color: "#8fa3b1" }}>
              {formatDate(weekStart)} — {formatDate(addDays(weekStart, 6))}
            </div>
          </div>

          <button onClick={goToday} style={{
            padding: "6px 14px", borderRadius: 7,
            border: "1.5px solid #163244", background: "transparent",
            color: "#163244", fontFamily: "Montserrat, sans-serif",
            fontWeight: 700, fontSize: 12, cursor: "pointer",
          }}>Hoy</button>

          <button onClick={nextWeek} style={{
            background: "none", border: "1.5px solid #e0e9ef",
            borderRadius: 7, width: 34, height: 34, cursor: "pointer",
            color: "#163244", fontSize: 16, display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>›</button>
        </div>

        {/* Loading / Error */}
        {loading && (
          <div style={{ textAlign: "center", padding: "60px", color: "#8fa3b1" }}>
            <span style={{
              display: "inline-block", width: 24, height: 24,
              border: "3px solid #cddae4", borderTopColor: "#163244",
              borderRadius: "50%", animation: "spin 0.7s linear infinite",
            }} />
          </div>
        )}
        {error && !loading && (
          <div style={{ textAlign: "center", padding: "40px", color: "#c0392b", fontSize: 14 }}>
            ⚠ {error}
          </div>
        )}

        {/* Grilla semanal */}
        {!loading && !error && calData && (
          <>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 8, marginBottom: 16,
            }}>
              {days.map((day, idx) => {
                const dayKey   = day.toDateString();
                const dayJobs  = jobsByDay[dayKey]   || [];
                const dayBlocks = blocksByDay[dayKey] || [];
                const avail    = availByDay[day.getDay()];
                const today    = isToday(day);
                const isAvailable = avail?.available ?? false;

                return (
                  <div key={idx} style={{
                    background: today ? "#f0f5f8" : "#fff",
                    borderRadius: 10,
                    border: today ? "2px solid #163244" : "1.5px solid #e0e9ef",
                    minHeight: 160,
                    overflow: "hidden",
                  }}>
                    {/* Cabecera del día */}
                    <div style={{
                      padding: "8px 10px",
                      background: today ? "#163244" : isAvailable ? "#f7fafc" : "#f0f0f0",
                      borderBottom: "1px solid #e0e9ef",
                      textAlign: "center",
                    }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                        color: today ? "#E7A432" : "#8fa3b1",
                      }}>
                        {DAYS_ES[day.getDay()].toUpperCase()}
                      </div>
                      <div style={{
                        fontSize: 18, fontWeight: 800,
                        color: today ? "#fff" : isAvailable ? "#163244" : "#aaa",
                      }}>
                        {day.getDate()}
                      </div>
                      {isAvailable && avail && (
                        <div style={{ fontSize: 9, color: today ? "#8fa3b1" : "#aab8c2", marginTop: 1 }}>
                          {avail.start_time.slice(0,5)}–{avail.end_time.slice(0,5)}
                        </div>
                      )}
                      {!isAvailable && (
                        <div style={{ fontSize: 9, color: "#c0a0a0" }}>No disponible</div>
                      )}
                    </div>

                    {/* Contenido del día */}
                    <div style={{ padding: "8px" }}>
                      {dayBlocks.map(block => (
                        <BlockChip
                          key={block.id}
                          block={block}
                          onClick={() => setBlockModal(block)}
                        />
                      ))}
                      {dayJobs.map(job => (
                        <JobCard
                          key={job.id}
                          job={job}
                          onClick={setSelectedJob}
                        />
                      ))}
                      {dayJobs.length === 0 && dayBlocks.length === 0 && isAvailable && (
                        <div style={{
                          fontSize: 10, color: "#cddae4",
                          textAlign: "center", padding: "12px 0",
                        }}>
                          Libre
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Jobs sin fecha */}
            {unscheduled.length > 0 && (
              <div style={{
                background: "#fff", borderRadius: 10,
                border: "1.5px dashed #E7A432",
                padding: "14px 16px", marginBottom: 16,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: "#b06a00",
                  letterSpacing: "0.06em", marginBottom: 10,
                }}>
                  ⏳ SIN FECHA CONFIRMADA ({unscheduled.length})
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {unscheduled.map(job => (
                    <div
                      key={job.id}
                      onClick={() => setSelectedJob(job)}
                      style={{
                        background: "#fff3e0", border: "1.5px solid #E7A432",
                        borderRadius: 8, padding: "8px 12px", cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      <div style={{ fontWeight: 700, color: "#163244" }}>
                        {SERVICE_LABELS[job.service_key] || job.service_key}
                      </div>
                      <div style={{ color: "#5c7a8a", fontSize: 11 }}>
                        {job.client_name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leyenda */}
            <div style={{
              display: "flex", gap: 16, flexWrap: "wrap",
              fontSize: 11, color: "#8fa3b1",
            }}>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: cfg.dot }} />
                  {cfg.label}
                </div>
              ))}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#c0392b" }} />
                Bloqueado
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {selectedJob && (
        <JobModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onSave={fetchWeek}
          onStatusChange={fetchWeek}
        />
      )}
      {blockModal !== false && (
        <BlockModal
          block={blockModal}
          onClose={() => setBlockModal(false)}
          onSave={fetchWeek}
          onDelete={fetchWeek}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
